import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Alert, Space, Typography, Switch, Tag, message, Row, Col, Divider } from 'antd';
import { SaveOutlined, SendOutlined, ReloadOutlined, GroupOutlined } from '@ant-design/icons';
import { adminAPI } from '../api/admin';

const { Paragraph, Title } = Typography;

export default function TelegramBotSettingsManager() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasBotToken, setHasBotToken] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getTelegramBotSettings();
      if (res.data.success) {
        const s = res.data.settings;
        setHasBotToken(s.hasBotToken);
        setStatus(res.data.status);
        
        const houseLinks = res.data.houseLinks || {};
        const schoolLink = res.data.schoolLink || {};

        form.setFieldsValue({
          enabled: s.enabled,
          botToken: '',
          channelUsername: s.channelUsername || '',
          
          schoolInviteLink: schoolLink.inviteLink || '',
          schoolChatId: schoolLink.chatId || '',
          
          Gryffindor_inviteLink: houseLinks.Gryffindor?.inviteLink || '',
          Gryffindor_chatId: houseLinks.Gryffindor?.chatId || '',
          
          Slytherin_inviteLink: houseLinks.Slytherin?.inviteLink || '',
          Slytherin_chatId: houseLinks.Slytherin?.chatId || '',
          
          Hufflepuff_inviteLink: houseLinks.Hufflepuff?.inviteLink || '',
          Hufflepuff_chatId: houseLinks.Hufflepuff?.chatId || '',
          
          Ravenclaw_inviteLink: houseLinks.Ravenclaw?.inviteLink || '',
          Ravenclaw_chatId: houseLinks.Ravenclaw?.chatId || ''
        });
      }
    } catch {
      message.error('خطا در بارگذاری تنظیمات ربات تلگرام');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const payload = {
        enabled: values.enabled,
        botToken: values.botToken,
        channelUsername: values.channelUsername,
        schoolLink: {
          inviteLink: values.schoolInviteLink,
          chatId: values.schoolChatId
        },
        houseLinks: {
          Gryffindor: {
            inviteLink: values.Gryffindor_inviteLink,
            chatId: values.Gryffindor_chatId
          },
          Slytherin: {
            inviteLink: values.Slytherin_inviteLink,
            chatId: values.Slytherin_chatId
          },
          Hufflepuff: {
            inviteLink: values.Hufflepuff_inviteLink,
            chatId: values.Hufflepuff_chatId
          },
          Ravenclaw: {
            inviteLink: values.Ravenclaw_inviteLink,
            chatId: values.Ravenclaw_chatId
          }
        }
      };

      const res = await adminAPI.updateTelegramBotSettings(payload);
      if (res.data.success) {
        message.success('تنظیمات تلگرام ذخیره شد');
        setStatus(res.data.status);
        loadSettings();
      } else {
        message.error(res.data.message || 'خطا در ذخیره');
      }
    } catch (e) {
      message.error(e?.response?.data?.message || 'خطا در اتصال');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 850 }}>
      <Alert
        type="info"
        showIcon
        icon={<SendOutlined />}
        style={{ marginBottom: 20 }}
        message="مدیریت تلگرام پاترهد"
        description={
          <Paragraph style={{ marginBottom: 0 }}>
            در این بخش می‌توانید ربات‌های تلگرام، کانال جوایز و گروه‌های مربوط به هر خانه یا کل مدرسه را مدیریت و پیکربندی کنید.
            ربات‌ها برای ایجاد لینک‌های یک‌بارمصرف اختصاصی و تأیید خودکار درخواست‌های ورود (Join Request) جادوگران به کار می‌روند.
          </Paragraph>
        }
      />

      {status && (
        <Alert
          type={status.connected ? 'success' : 'warning'}
          showIcon
          style={{ marginBottom: 20 }}
          message={
            <Space wrap>
              <span>وضعیت اتصال ربات:</span>
              <Tag color={status.connected ? 'green' : 'default'}>{status.connected ? 'متصل' : 'قطع'}</Tag>
              {status.botUsername && <Tag color="blue">@{status.botUsername}</Tag>}
              {status.channelUsername && <Tag>{status.channelUsername}</Tag>}
              {status.lastError && <Tag color="red">{status.lastError}</Tag>}
            </Space>
          }
          action={<Button size="small" icon={<ReloadOutlined />} onClick={loadSettings}>بروزرسانی وضعیت</Button>}
        />
      )}

      <Form form={form} layout="vertical" onFinish={handleSave} style={{ fontFamily: "'Vazirmatn', sans-serif" }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card
              loading={loading}
              title={
                <Space>
                  <SendOutlined />
                  <span>پیکربندی ربات تلگرام</span>
                </Space>
              }
              style={{ height: '100%' }}
            >
              <Form.Item name="enabled" label="فعال بودن ربات تلگرام" valuePropName="checked">
                <Switch checkedChildren="فعال" unCheckedChildren="غیرفعال" />
              </Form.Item>

              <Form.Item
                name="botToken"
                label="توکن ربات (Bot Token)"
                help={
                  hasBotToken
                    ? 'توکن ذخیره‌شده وجود دارد. برای تغییر، توکن جدید وارد کن؛ در غیر این‌صورت خالی بگذار.'
                    : 'توکن را از @BotFather دریافت کن (مثال: 123456:ABC-DEF...)'
                }
              >
                <Input.Password
                  placeholder={hasBotToken ? '••••••••••••• (بدون تغییر)' : 'توکن ربات را وارد کن'}
                  style={{ fontFamily: 'monospace', direction: 'ltr' }}
                />
              </Form.Item>

              <Form.Item
                name="channelUsername"
                label="یوزرنیم کانال تلگرام اصلی"
                help="کانالی که عضویتش برای تسک جایزه بررسی می‌شود — ربات باید ادمین آن باشد"
              >
                <Input placeholder="@WwWPotterHeadIR" style={{ fontFamily: 'monospace', direction: 'ltr' }} />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} md={12}>
            <Card
              loading={loading}
              title={
                <Space>
                  <GroupOutlined />
                  <span>لینک گروه‌ها و کانال‌های فرعی</span>
                </Space>
              }
              style={{ height: '100%' }}
            >
              <Title level={5} style={{ marginTop: 0 }}>🏰 گروه عمومی مدرسه هاگوارتز</Title>
              <Row gutter={8}>
                <Col span={14}>
                  <Form.Item name="schoolInviteLink" label="لینک دعوت گروه مدرسه" style={{ marginBottom: 12 }}>
                    <Input placeholder="https://t.me/+..." style={{ fontFamily: 'monospace', direction: 'ltr' }} />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item name="schoolChatId" label="شناسه عددی چت (Chat ID)" style={{ marginBottom: 12 }}>
                    <Input placeholder="-100..." style={{ fontFamily: 'monospace', direction: 'ltr' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider style={{ margin: '12px 0' }} />
              
              <Title level={5}>🛡️ گروه‌های اختصاصی خانه‌ها</Title>
              
              {/* Gryffindor */}
              <Paragraph style={{ marginBottom: 4, fontWeight: 'bold' }}>🦁 گریفیندور</Paragraph>
              <Row gutter={8}>
                <Col span={14}>
                  <Form.Item name="Gryffindor_inviteLink" style={{ marginBottom: 8 }}>
                    <Input placeholder="لینک دعوت گریفیندور" style={{ fontFamily: 'monospace', direction: 'ltr' }} />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item name="Gryffindor_chatId" style={{ marginBottom: 8 }}>
                    <Input placeholder="شناسه چت" style={{ fontFamily: 'monospace', direction: 'ltr' }} />
                  </Form.Item>
                </Col>
              </Row>

              {/* Slytherin */}
              <Paragraph style={{ marginBottom: 4, fontWeight: 'bold' }}>🐍 اسلیترین</Paragraph>
              <Row gutter={8}>
                <Col span={14}>
                  <Form.Item name="Slytherin_inviteLink" style={{ marginBottom: 8 }}>
                    <Input placeholder="لینک دعوت اسلیترین" style={{ fontFamily: 'monospace', direction: 'ltr' }} />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item name="Slytherin_chatId" style={{ marginBottom: 8 }}>
                    <Input placeholder="شناسه چت" style={{ fontFamily: 'monospace', direction: 'ltr' }} />
                  </Form.Item>
                </Col>
              </Row>

              {/* Hufflepuff */}
              <Paragraph style={{ marginBottom: 4, fontWeight: 'bold' }}>🦡 هافلپاف</Paragraph>
              <Row gutter={8}>
                <Col span={14}>
                  <Form.Item name="Hufflepuff_inviteLink" style={{ marginBottom: 8 }}>
                    <Input placeholder="لینک دعوت هافلپاف" style={{ fontFamily: 'monospace', direction: 'ltr' }} />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item name="Hufflepuff_chatId" style={{ marginBottom: 8 }}>
                    <Input placeholder="شناسه چت" style={{ fontFamily: 'monospace', direction: 'ltr' }} />
                  </Form.Item>
                </Col>
              </Row>

              {/* Ravenclaw */}
              <Paragraph style={{ marginBottom: 4, fontWeight: 'bold' }}>🦅 ریونکلا</Paragraph>
              <Row gutter={8}>
                <Col span={14}>
                  <Form.Item name="Ravenclaw_inviteLink" style={{ marginBottom: 12 }}>
                    <Input placeholder="لینک دعوت ریونکلا" style={{ fontFamily: 'monospace', direction: 'ltr' }} />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item name="Ravenclaw_chatId" style={{ marginBottom: 12 }}>
                    <Input placeholder="شناسه چت" style={{ fontFamily: 'monospace', direction: 'ltr' }} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Form.Item style={{ marginTop: 24, textAlign: 'center' }}>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving} size="large" style={{ width: 220 }}>
            ذخیره کل تنظیمات
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
}
